/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo, ReactElement } from "react";
import axios from "axios";
import Card from "components/card";
import InputField from "components/fields/InputField";
import {
  Select,
  Button,
  Badge,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FaAngleLeft } from "react-icons/fa";

import SweetAlert2 from "react-sweetalert2";
import { BsPersonCheck } from "react-icons/bs";
import { Tooltip, IconButton } from "@mui/material";

import ReceiverDetailTab from "./components/ReceiverDetailTab";
import ReceiverAccountsTab from "./components/ReceiverAccountsTab";
import ReceiverPayoutHistoryTab from "./components/ReceiverPayoutHistoryTab";
import ReceiverResendKYCConfirmModal from "./components/ReceiverResendKYCConfirmModal";
import { useParams, useNavigate } from "react-router-dom";

const ReceiverDetail = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenResendKYCConfirmModal, setIsOpenResendKYCConfirmModal] =
    useState<boolean>(false);
  const [receiverDetailForEdit, setReceiverDetailForEdit] = useState(null);
  const [currentTab, setCurrentTab] = useState<string>("details");
  const [countries, setCountries] = useState<any>(null);
  const [dialcodeList, setDialcodeList] = useState<any>(null);
  const [swalProps, setSwalProps] = useState<any>({});
  const navigate = useNavigate();
  const { id } = useParams();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  useEffect(() => {
    const fetchData = async () => {
      await fetchReceiverDetail(id);
    };

    fetchData();
  }, []);

  const fetchReceiverDetail = async (receiverId: string) => {
    try {
      setReceiverDetailForEdit(null);

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/receiver/func/get-receiver-by-id?receiverid=${receiverId}`
      );

      let receiverData = response.data.receiver?.payload;

      // Check if receiver data exists, if not redirect to receiver listing
      if (!receiverData) {
        console.error("Receiver not found");
        navigate(`/${userRole}/receiver`);
        return;
      }

      setReceiverDetailForEdit(receiverData);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching data", error);
      // Redirect to receiver listing page on error
      navigate(`/${userRole}/receiver`);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  interface CustomerStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: CustomerStatusItems[] = [
    {
      status: "creating",
      value: "creating",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Creating</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "completed",
      value: "completed",
      display: (
        <Badge className={`${statusClasses} status-completed`}>Completed</Badge>
      ),
    },
    {
      status: "active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const statusDisplay = (status: string) => {
    const statusItem = statusList.find((item) => item.status === status);
    return statusItem ? statusItem.display : null;
  };

  const kycStatusList: CustomerStatusItems[] = [
    {
      status: "creating",
      value: "creating",
      display: (
        <Badge className={`${statusClasses} status-initializing`}>Creating</Badge>
      ),
    },
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "completed",
      value: "completed",
      display: (
        <Badge className={`${statusClasses} status-completed`}>Completed</Badge>
      ),
    },
    {
      status: "active",
      value: "active",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "inactive",
      value: "inactive",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
    {
      status: "failed",
      value: "failed",
      display: (
        <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
      ),
    },
  ];

  const kycStatusDisplay = (status: string) => {
    const statusItem = kycStatusList.find((item) => item.status === status);
    return statusItem ? statusItem.display : null;
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/countries`
      );

      let countries = response?.data;
      let dialcodeList: any = [];
      if (countries.length > 0) {
        countries.forEach((c: any) => {
          if (c.dialcode !== null) {
            if (c.dialcode in dialcodeList) {
              dialcodeList[c.dialcode] =
                dialcodeList[c.dialcode] + ", " + c.country_name;
            } else {
              dialcodeList[c.dialcode] =
                "(+" + c.dialcode + ") " + c.country_name;
            }
          }
        });
      }

      const sortedDialcodeList: any = [];
      Object.keys(dialcodeList)
        .sort()
        .forEach((key: any) => {
          sortedDialcodeList[key] = dialcodeList[key];
        });

      setCountries(countries);
      setDialcodeList(sortedDialcodeList);
    } catch (error) {
      console.error("Error fetching countries", error);
    }
  };

  const handleClickBack = () => {
    navigate(`/${userRole}/receiver`);
  };

  const handleResendKYC = (id: any) => {
    showResendKYCConfirmModal();
  };

  const showResendKYCConfirmModal = () => {
    setIsOpenResendKYCConfirmModal(true);
  };

  const closeResendKYCConfirmModal = () => {
    setIsOpenResendKYCConfirmModal(false);
  };

  const triggerReloadDetail = async () => {
    setIsLoading(true);
    await fetchReceiverDetail(id);
  };

  // Define tab configuration
  const tabConfig = useMemo(
    () => [
      { label: "Details", show: true },
      { label: "Accounts", show: true },
      { label: "Payout History", show: true },
    ],
    []
  );

  // Filter visible tabs (currently all are visible, but ready for permissions)
  const visibleTabs = useMemo(() => {
    return tabConfig.filter((tab) => tab.show);
  }, [tabConfig]);

  // Render tab content based on label
  const renderTabContent = (label: string) => {
    switch (label) {
      case "Details":
        return (
          <ReceiverDetailTab
            detail={receiverDetailForEdit}
            setSwalProps={setSwalProps}
            countries={countries}
            dialcodeList={dialcodeList}
            userPermissions={userPermissions}
          />
        );
      case "Accounts":
        return (
          <ReceiverAccountsTab
            detail={receiverDetailForEdit}
            userPermissions={userPermissions}
          />
        );
      case "Payout History":
        return <ReceiverPayoutHistoryTab detail={receiverDetailForEdit} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Card className="card !rounded-xl">
        {!isLoading &&
        receiverDetailForEdit !== null &&
        countries !== null &&
        dialcodeList !== null ? (
          <>
            <ReceiverResendKYCConfirmModal
              setSwalProps={setSwalProps}
              triggerReload={triggerReloadDetail}
              isOpen={isOpenResendKYCConfirmModal}
              receiverToResendKYC={receiverDetailForEdit}
              onClose={() => closeResendKYCConfirmModal()}
            />

            <div className="flex items-center border border-gray-200 p-8 py-6">
              <div>
                <button
                  type="button"
                  onClick={handleClickBack}
                  className="
                  mb-1 inline-flex items-center gap-1
                  text-sm font-medium text-gray-700
                  transition hover:text-gray-700
                  disabled:cursor-not-allowed disabled:opacity-40
                  dark:text-gray-400 dark:hover:text-white
                "
                >
                  <FaAngleLeft className="text-base" />
                  <span>Receivers</span>
                </button>

                <h3 className="text-xl font-semibold text-navy-700">
                  Receiver ({receiverDetailForEdit.id})
                </h3>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 p-8 pt-6 text-sm font-medium text-navy-700 dark:text-white md:grid-cols-2">
              <div className="flex items-center">
                <span className="mr-1 w-28">KYC Status:</span>
                {kycStatusDisplay(receiverDetailForEdit.kyc_status)}
                {(receiverDetailForEdit.kyc_status === "pending" ||
                  receiverDetailForEdit.kyc_status === "failed") &&
                  userRole === "superadmin" && (
                    <Tooltip title="Resend KYC Link" arrow placement="top">
                      <IconButton
                        className="ml-2"
                        id={`button-resend-kyc-${receiverDetailForEdit.id}`}
                        onClick={() =>
                          handleResendKYC(receiverDetailForEdit.id)
                        }
                      >
                        <BsPersonCheck className="h-6 w-6 text-navy-700" />
                      </IconButton>
                    </Tooltip>
                  )}
              </div>

              <div className="flex items-center">
                <span className="mr-1 w-28">Status:</span>
                {statusDisplay(receiverDetailForEdit.status)}
              </div>

              {userRole === "superadmin" && receiverDetailForEdit.merchant && (
                <div className="flex items-center">
                  <span className="mr-1 w-28">Merchant:</span>
                  <span className="font-normal">
                    {receiverDetailForEdit.merchant.name}
                  </span>
                </div>
              )}

              {userRole === "superadmin" &&
                receiverDetailForEdit.subaccount && (
                  <div className="flex items-center">
                    <span className="mr-1 w-28">Subaccount:</span>
                    <span className="font-normal">
                      {receiverDetailForEdit.subaccount.name}
                    </span>
                  </div>
                )}
            </div>
          </>
        ) : (
          <div className="p-8 py-6">
            <div
              className="mb-3 mt-3 h-5 w-40 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 mt-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
          </div>
        )}
      </Card>

      <Tabs className="card mt-3 !rounded-xl">
        <TabList className="rounded-tl-custom rounded-tr-custom scrollbar-hide flex flex-wrap overflow-x-auto sm:flex-nowrap">
          {visibleTabs.map((tab, index) => (
            <Tab
              key={index}
              className={`child-tab rounded-tl-custom rounded-tr-custom sm:text-md whitespace-nowrap bg-white px-4 py-3 pt-4 text-sm text-navy-700 sm:px-6 sm:py-4 sm:pt-6 ${
                isLoading || receiverDetailForEdit === null
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {tab.label}
            </Tab>
          ))}
        </TabList>

        <TabPanels className="rounded-bl-custom rounded-br-custom border-t border-gray-200 bg-white">
          {visibleTabs.map((tab, index) => (
            <TabPanel
              key={index}
              className={
                tab.label === "Details"
                  ? "rounded-bl-custom rounded-br-custom p-8"
                  : ""
              }
            >
              {renderTabContent(tab.label)}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default ReceiverDetail;
